import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Info } from "lucide-react";
import { ConfidenceBar } from "./ConfidenceBar";
const SIGNALS = [
    {
        scoreKey: "applicability",
        weightKey: "weight_applicability",
        label: "Applicability",
        description: "How strongly this claim's matched precedent fits: rewards both breadth (multiple independent matching channels agreeing) and depth (how strong each channel's best match is).",
    },
    {
        scoreKey: "legal_stability",
        weightKey: "weight_legal_stability",
        label: "Legal stability",
        description: "How settled the matched ruling is - its appeal-chain authority status (final/upheld scores highest, reversed scores zero), scaled by how binding that ruling is on the forum governing this contract.",
    },
    {
        scoreKey: "recoverability",
        weightKey: "weight_recoverability",
        label: "Recoverability",
        description: "Whether a procedural door exists to reopen this claim if the underlying disallowance is already settled - e.g. a change-in-law or reopening clause in the contract. Full score if nothing is settled yet.",
    },
    {
        scoreKey: "limitation",
        weightKey: "weight_limitation",
        label: "Limitation",
        description: "How much of the filing limitation window remains. Full score when untouched, falling as the deadline nears, and zero once the claim is flagged time-barred.",
    },
    {
        scoreKey: "quantification",
        weightKey: "weight_quantification",
        label: "Quantification",
        description: "How confidently the rupee amount is priced: scaled by the claimed value while still in the pipeline, or anchored on the tariff ledger once the claim is actually recovered.",
    },
];
/**
 * The weighted-sum arithmetic behind `values.confidence_score`, with this
 * claim's real numbers substituted in - e.g. "25% x Applicability (38%) +
 * 25% x Legal stability (85%) + 20% x Recoverability (100%) + 15% x
 * Limitation (0%) + 15% x Quantification (99%) = 67%". Only the signals
 * that actually counted (weight > 0) appear, the same filter the score
 * bars themselves use, so the formula never lists a term the score didn't
 * use.
 *
 * A time-barred claim's score is not this plain sum - `matching.scoring`
 * scales the same weighted merit down into a low cap (see `status_note`) -
 * so the raw sum and `confidence_score` are compared, and if they don't
 * match (within rounding) the formula is shown as the uncapped merit with
 * an explicit note of what it was then scaled to, rather than asserting a
 * false equality.
 */
export function formatConfidenceFormula(values) {
    const terms = SIGNALS.filter(({ weightKey }) => values[weightKey] > 0).map(({ scoreKey, weightKey, label }) => {
        const weightPct = Math.round(values[weightKey] * 100);
        const scorePct = Math.round(values[scoreKey] * 100);
        return { text: `${weightPct}% x ${label} (${scorePct}%)`, raw: values[weightKey] * values[scoreKey] };
    });
    const rawSum = terms.reduce((sum, term) => sum + term.raw, 0);
    const rawPct = Math.round(rawSum * 100);
    const totalPct = Math.round(values.confidence_score * 100);
    const sumText = terms.map((t) => t.text).join(" + ");
    if (rawPct === totalPct) {
        return `${sumText} = ${totalPct}%`;
    }
    return `${sumText} = ${rawPct}% merit, scaled to ${totalPct}% (time-barred cap)`;
}
export function RecoveryConfidenceBreakdown({ values, className = "", }) {
    return (_jsxs("div", { className: ["flex flex-col gap-4", className].join(" "), children: [_jsx("div", { className: "grid grid-cols-1 gap-4 sm:grid-cols-2", children: SIGNALS.map(({ scoreKey, weightKey, label, description }) => {
                    const score = values[scoreKey];
                    const weight = values[weightKey];
                    if (typeof score !== "number" || weight <= 0)
                        return null;
                    return (_jsxs("div", { children: [_jsxs("div", { className: "mb-1 flex items-center justify-between gap-2 text-xs font-medium text-slate-500", children: [_jsxs("span", { className: "group relative inline-flex items-center gap-1", children: [_jsxs("span", { className: "inline-flex cursor-help items-center gap-1", children: [label, _jsx(Info, { className: "h-3 w-3 text-slate-400", "aria-hidden": true })] }), _jsx("span", { className: "sr-only", children: description }), _jsx("span", { role: "tooltip", className: "pointer-events-none absolute bottom-full left-0 z-50 mb-1.5 w-56 scale-95 rounded-md bg-zinc-900 px-2.5 py-1.5 text-[0.6875rem] font-normal leading-snug text-white opacity-0 shadow-lg transition duration-100 group-hover:scale-100 group-hover:opacity-100", children: description })] }), _jsxs("span", { className: "font-mono tabular-nums text-slate-400", children: [Math.round(weight * 100), "% weight"] })] }), _jsx(ConfidenceBar, { value: Math.round(score * 100) })] }, scoreKey));
                }) }), values.status_note ? (_jsx("p", { className: "rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600", children: values.status_note })) : null] }));
}
